import React, { useState } from "react";
import { motion } from "framer-motion";
import { Reports } from "../../schedule/types/types";
import {
  LuCalendarDays,
  LuUser,
  LuTarget,
  LuPackage,
  LuClipboard,

  LuUserCheck,
  LuClock,
  LuX,
  LuChevronRight,
  LuCalendarClock,
  LuBuilding,
  LuSunMedium,
  LuMoon,
  LuSunrise,
  LuSunset,
  LuPhone,
  LuFileText,
} from "react-icons/lu";
import accessURL from "../../../../components/utils/access";
import { toast } from "react-toastify";
import {
  containerVariants,
  listItemVariants,
  modalVariants,
} from "../../../../animations/Animations";
import { useQuery } from "@tanstack/react-query";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  report: Reports | null;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, report }) => {
  const [isEditingComment, setIsEditingComment] = useState(false);
  const [editedComment, setEditedComment] = useState("");
  const [loading, setLoading] = useState(false);

  const { data } = useQuery({
    queryKey: ["customer-seen", report?.customer],
    enabled: isOpen && !!report?.customer,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (report?.customer) params.append("customer", report?.customer);

      const response = await accessURL(`customer-seen?${params.toString()}`);
      return response;
    },
  });

  if (!isOpen || !report) return null;

  const parseItems = (value: string[] | string | null | undefined): string[] => {
    if (Array.isArray(value)) return value;
    if (!value) return [];
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [String(parsed)];
    } catch {
      return [value];
    }
  };
  const products = parseItems(report.product);
  const contents = parseItems(report.content);

  const categoryColor = (category: string) => {
    switch (category) {
      case "A":
        return "bg-emerald-50 text-emerald-700 ring-emerald-600/20";
      case "B":
        return "bg-amber-50 text-amber-700 ring-amber-600/20";
      case "C":
        return "bg-rose-50 text-rose-700 ring-rose-600/20";
      default:
        return "bg-gray-50 text-gray-700 ring-gray-600/20";
    }
  };

  const getTimeIcon = (time: string) => {
    const hour = parseInt(time.split(":")[0]);
    if (hour >= 5 && hour < 12)
      return <LuSunrise className="text-amber-500 w-5 h-5" />;
    if (hour >= 12 && hour < 17)
      return <LuSunMedium className="text-orange-500 w-5 h-5" />;
    if (hour >= 17 && hour < 20)
      return <LuSunset className="text-purple-500 w-5 h-5" />;
    return <LuMoon className="text-indigo-500 w-5 h-5" />;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleEditComment = () => {
    setEditedComment(report.comment);
    setIsEditingComment(true);
  };

  const handleSaveComment = async () => {
    setLoading(true);
    const response = await accessURL.post("/reports/update", {
      comment: editedComment,
      id: report.id,
    });

    setIsEditingComment(false);

    if (response.status === 200) {
      toast.success("Comment updated successfully!");
      setLoading(false);
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } else {
      toast.error("Failed to update comment.");
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      exit="exit"
      variants={modalVariants}
      className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex justify-center items-center z-50 p-4 animate-in fade-in duration-200"
    >
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <motion.div
          className="border-b border-gray-100 p-6 sticky top-0 bg-gradient-to-r from-blue-50 via-indigo-50 to-blue-50"
          variants={listItemVariants}
        >
          <div className="flex justify-between items-start">
            <div className="space-y-1">
              <h2 className="text-2xl font-semibold text-gray-900">
                Visit Report
              </h2>
              <p className="text-sm text-gray-500">
                Created on {formatDate(report.created_at)}
              </p>
            </div>
            <button
              onClick={onClose}
              className="rounded-full p-2 hover:bg-white/50 transition-colors text-gray-500 hover:text-gray-700"
            >
              <LuX className="w-5 h-5" />
            </button>
          </div>
        </motion.div>

        {/* Content */}
        <motion.div
          className="p-6 space-y-8 overflow-y-auto max-h-[calc(90vh-200px)]"
          variants={listItemVariants}
        >
          {/* Summary Section (New Addition) */}
          <motion.div
            className="bg-gradient-to-r from-blue-400/10 to-indigo-400/10 rounded-xl p-6 border border-blue-100 shadow-sm"
            variants={listItemVariants}
          >
            <div className="flex items-start gap-4">
              <div className="p-2 bg-white rounded-md shadow-sm flex-shrink-0">
                <LuFileText className="text-blue-600 w-5 h-5" />
              </div>
              <div className="flex-grow">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Discussion Summary
                </h3>
                <div className="bg-white/70 rounded-lg p-4">
                  <p className="text-gray-700 text-sm leading-relaxed">
                    {report.summary ? (
                      report.summary
                    ) : (
                      <>
                        Visit to{" "}
                        <span className="font-medium">{report.customer}</span>{" "}
                        (Category {report.customerCategory}) on{" "}
                        {formatDate(report.dateOfVisit)} at {report.timeOfVisit}{" "}
                        conducted by {report.salesRep}. The main objective was
                        to {report.objective?.substring(0, 100)}
                        {report.objective?.length > 100 ? "..." : ""}. Discussed{" "}
                        {products?.length} products and covered{" "}
                        {contents?.length} content items.
                        {report.followUpPlan
                          ? " Follow-up plan has been established."
                          : ""}
                      </>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Customer Info Section */}
          <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl p-6 space-y-4">
            <div className="flex flex-col items-center justify-between md:flex-row">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-white rounded-lg shadow-sm">
                  <LuUser className="text-violet-600 w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    {report.customer}
                  </h3>
                </div>
              </div>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ring-1 ring-inset ${categoryColor(
                  report.customerCategory
                )}`}
              >
                Category {report.customerCategory}
              </span>
            </div>

            <div className="flex items-center gap-2 text-gray-600 pt-2">
              <LuUserCheck className="text-violet-600" />
              <span>
                Sales Representative:{" "}
                <span className="font-medium">{report.salesRep}</span>
              </span>
            </div>
          </div>

          {/* Visit Details Grid */}
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
            variants={listItemVariants}
          >
            <div className="space-y-6">
              <motion.div
                className="flex items-start gap-4 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg hover:from-emerald-100 hover:to-teal-100 transition-colors"
                variants={listItemVariants}
              >
                <div className="p-2 bg-white rounded-md shadow-sm">
                  <LuCalendarDays className="text-emerald-600 w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Date of Visit
                  </p>
                  <p className="text-gray-900 font-medium">
                    {formatDate(report.dateOfVisit)}
                  </p>
                </div>
              </motion.div>

              <motion.div
                className="flex items-start gap-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg hover:from-amber-100 hover:to-orange-100 transition-colors"
                variants={listItemVariants}
              >
                <div className="p-2 bg-white rounded-md shadow-sm">
                  {getTimeIcon(report.timeOfVisit)}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Time of Visit
                  </p>
                  <p className="text-gray-900 font-medium">
                    {report.timeOfVisit}
                  </p>
                </div>
              </motion.div>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg hover:from-purple-100 hover:to-pink-100 transition-colors">
                <div className="p-2 bg-white rounded-md shadow-sm">
                  <LuBuilding className="text-purple-600 w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Achieved Visit
                  </p>
                  <p className="text-gray-900 font-medium">
                    {data?.data?.number?.reportsCount ?? 0}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-rose-50 to-red-50 rounded-lg hover:from-rose-100 hover:to-red-100 transition-colors">
                <div className="p-2 bg-white rounded-md shadow-sm">
                  <LuClock className="text-rose-600 w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Created At
                  </p>
                  <p className="text-gray-900">
                    {formatDate(report.created_at)}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-rose-50 to-red-50 rounded-lg hover:from-rose-100 hover:to-red-100 transition-colors">
                <div className="p-2 bg-white rounded-md shadow-sm">
                  <LuPhone className="text-rose-600 w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Call Rate</p>
                  <p className="text-gray-900">
                    {parseFloat(data?.data.number.callRate) === 0
                      ? "0%"
                      : `${(
                        parseFloat(data?.data.number.callRate) * 100
                      ).toFixed(2)}%`}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Products & Content */}
          <motion.div
            className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6"
            variants={listItemVariants}
          >
            <div className="flex items-start gap-4">
              <div className="p-2 bg-white rounded-md shadow-sm flex-shrink-0">
                <LuTarget className="text-blue-600 w-5 h-5" />
              </div>
              <div className="flex-grow">
                <p className="text-sm font-medium text-gray-600 mb-2">
                  Objective
                </p>
                <p className="text-gray-900 whitespace-pre-wrap break-words">
                  {report.objective}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div className="space-y-6" variants={listItemVariants}>
            <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <LuPackage className="text-cyan-600 w-5 h-5" />
                <h4 className="font-medium text-gray-900">
                  Products Discussed
                </h4>
              </div>
              <div className="flex flex-wrap gap-2">
                {products.map((product: string, index: number) => (
                  <span
                    key={index}
                    className="bg-white text-cyan-700 px-3 py-1.5 rounded-full text-sm font-medium ring-1 ring-inset ring-cyan-700/10 flex items-center gap-1"
                  >
                    {product}
                    <LuChevronRight className="w-4 h-4 text-cyan-400" />
                  </span>
                ))}
              </div>
            </div>

            <motion.div
              className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl p-5 shadow-sm"
              variants={listItemVariants}
            >
              <div className="flex items-center gap-2 mb-4">
                <LuClipboard className="text-teal-600 w-5 h-5" />
                <h4 className="font-medium text-gray-900">Content Covered</h4>
              </div>
              <div className="flex flex-wrap gap-2">
                {contents.map((content: string, index: number) => (
                  <span
                    key={index}
                    className="bg-white text-teal-700 px-3 py-1.5 rounded-full text-sm font-medium ring-1 ring-inset ring-teal-700/10"
                  >
                    {content}
                  </span>
                ))}
              </div>
            </motion.div>
          </motion.div>

          {/* Follow-up Plan Section */}
          {report.followUpPlan && (
            <motion.div
              className="bg-gradient-to-r from-indigo-50 to-violet-50 rounded-xl p-6"
              variants={listItemVariants}
            >
              <motion.div
                className="flex items-start gap-4 p-4 bg-white/50 rounded-lg"
                variants={listItemVariants}
              >
                <div className="p-2 bg-white rounded-md shadow-sm">
                  <LuCalendarClock className="text-indigo-600 w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Follow-up Plan
                  </p>
                  <p className="text-gray-900 whitespace-pre-wrap break-words">
                    {report.followUpPlan}
                  </p>
                </div>
              </motion.div>
            </motion.div>
          )}
        </motion.div>

        {/* Footer */}
        <motion.div
          className="border-t border-gray-100 p-6 bg-gradient-to-r from-gray-50 to-slate-50"
          variants={listItemVariants}
        >
          {loading && (
            <svg
              role="status"
              className="inline w-5 h-5 mr-2 text-blue-600 animate-spin"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M50 100C77.614 100 100 77.614 100 50C100 22.386 77.614 0 50 0C22.386 0 0 22.386 0 50C0 77.614 22.386 100 50 100Z"
                fill="#e5e7eb"
              />
              <path
                d="M93.9716 50C93.9716 75.5097 75.5097 93.9716 50 93.9716C24.4903 93.9716 6.02844 75.5097 6.02844 50C6.02844 24.4903 24.4903 6.02844 50 6.02844"
                stroke="currentColor"
                strokeWidth="10"
                strokeLinecap="round"
              />
            </svg>
          )}

          <button
            onClick={onClose}
            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors shadow-sm hover:shadow-md flex items-center justify-center gap-2"
          >
            Close Report
            <LuX className="w-4 h-4" />
          </button>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default Modal;
